import { useCurrentFacility } from '@/hooks/useCurrentFacility'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronRight, DoorOpen, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useFacilityLocations } from '@/hooks/useFacility'
import { indexRooms } from '@/lib/facilityLocation'
import type { LocationRoom } from '@/api/facilityApi'
import { BuildingThumb } from './BuildingElevation'

/**
 * Choose where a unit stands by walking the site (z8nrz7fw25): the facility,
 * then one of its buildings, then a floor, then a room — each step a picture
 * of the level, not a long list. The chosen path reads across the top, so the
 * answer is visible before it is saved.
 *
 * Opens on the room the unit is already in, when it has one.
 */
export function PlacePicker({
  value,
  onChange,
  disabled,
}: {
  value: string | null
  onChange: (roomId: string | null) => void
  disabled?: boolean
}) {
  const locations = useFacilityLocations()
  const tree = locations.data
  // The app's facility (z8nrz7fw25), unless this picker opened on a room elsewhere.
  const [currentFacility, setCurrentFacility] = useCurrentFacility()
  const [localFacility, setFacilityId] = useState<string | null>(null)
  const facilityId = localFacility ?? currentFacility
  const [buildingId, setBuildingId] = useState<string | null>(null)
  const [floorId, setFloorId] = useState<string | null>(null)

  // Open on the current room's facility, building and floor.
  const at = useMemo(() => (tree && value ? indexRooms(tree).get(value) : undefined), [tree, value])
  // Once per room chosen, not on every reload: browsing another facility in
  // the picker must not snap back when something else saves.
  const openedOn = useRef<string | null>(null)
  useEffect(() => {
    if (!at || at.length < 4 || openedOn.current === value) return
    openedOn.current = value
    setFacilityId(at[0].id)
    setBuildingId(at[1].id)
    setFloorId(at[2].id)
  }, [at, value])

  if (locations.isLoading) {
    return (
      <p className='flex items-center gap-2 py-4 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Loading the site…
      </p>
    )
  }
  if (!tree || (tree.facilities.length === 0 && tree.unmappedRooms.length === 0)) {
    return <p className='py-3 text-sm text-muted-foreground'>No facility is set up yet. Use “Set up facility” to add one.</p>
  }

  const facility = tree.facilities.find((f) => f.id === facilityId) ?? tree.facilities[0] ?? null
  const building = facility?.buildings.find((b) => b.id === buildingId) ?? facility?.buildings[0] ?? null
  const floors = building ? [...building.floors].sort((a, b) => b.level - a.level) : []
  const floor = floors.find((f) => f.id === floorId) ?? floors.find((f) => f.rooms.length > 0) ?? floors[0] ?? null

  const chosenRoom = value ? (at ?? null) : null

  return (
    <div className={cn('space-y-3', disabled && 'pointer-events-none opacity-60')}>
      {/* The answer so far. */}
      <div className='flex min-h-9 flex-wrap items-center gap-1 rounded-lg border bg-slate-50 px-3 py-1.5 text-sm'>
        {chosenRoom ? (
          <>
            {chosenRoom.map((s, i) => (
              <span key={s.id} className='flex items-center gap-1'>
                {i > 0 && <ChevronRight className='h-3.5 w-3.5 text-slate-400' />}
                <span className={cn(i === chosenRoom.length - 1 ? 'font-semibold text-primary' : 'text-slate-700')}>{s.label}</span>
              </span>
            ))}
            <button
              type='button'
              className='ml-auto rounded p-1 text-slate-400 hover:bg-white hover:text-slate-700'
              aria-label='Clear the room'
              onClick={() => onChange(null)}
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </>
        ) : (
          <span className='text-slate-500'>Not in a room yet. Choose one below.</span>
        )}
      </div>

      {tree.facilities.length > 1 && (
        <div role='tablist' aria-label='Facility' className='inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1'>
          {tree.facilities.map((f) => (
            <button
              key={f.id}
              type='button'
              role='tab'
              aria-selected={f.id === facility?.id}
              onClick={() => {
                setFacilityId(f.id)
                setCurrentFacility(f.id)
                setBuildingId(null)
                setFloorId(null)
              }}
              className={cn(
                'rounded-md px-2.5 py-1 text-sm',
                f.id === facility?.id ? 'bg-white font-medium shadow-sm' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              <span className='font-bold'>{f.code}</span> <span className='hidden sm:inline'>{f.name}</span>
            </button>
          ))}
        </div>
      )}

      <div className='grid gap-3 sm:grid-cols-3'>
        <Step title={facility ? `Buildings in ${facility.code}` : 'Buildings'}>
          {facility?.buildings.length ? (
            facility.buildings.map((b) => (
              <BuildingThumb
                key={b.id}
                building={b}
                selected={b.id === building?.id}
                onSelect={() => {
                  setBuildingId(b.id)
                  setFloorId(null)
                }}
              />
            ))
          ) : (
            <Empty>No buildings yet.</Empty>
          )}
        </Step>

        <Step title={building ? `Floors of ${building.name}` : 'Floors'}>
          {floors.length ? (
            <div className='overflow-hidden rounded-xl border border-slate-300'>
              <div className='h-1.5 bg-slate-700' aria-hidden />
              {floors.map((f, i) => (
                <button
                  key={f.id}
                  type='button'
                  onClick={() => setFloorId(f.id)}
                  aria-pressed={f.id === floor?.id}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-2.5 py-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary',
                    i > 0 && 'border-t border-slate-200',
                    f.id === floor?.id ? 'bg-primary/10' : 'bg-white hover:bg-slate-50',
                  )}
                >
                  <span className='w-6 text-center text-base font-semibold tabular-nums text-slate-700'>{f.level}</span>
                  <span className='min-w-0 flex-1 truncate'>{f.name}</span>
                  <span className='text-[11px] text-slate-500'>{f.rooms.length}</span>
                </button>
              ))}
            </div>
          ) : (
            <Empty>No floors yet.</Empty>
          )}
        </Step>

        <Step title={floor ? `Rooms on ${floor.name}` : 'Rooms'}>
          {floor?.rooms.length ? (
            floor.rooms.map((r) => <RoomChoice key={r.id} room={r} selected={r.id === value} onSelect={() => onChange(r.id)} />)
          ) : (
            <Empty>No rooms on this floor.</Empty>
          )}
        </Step>
      </div>

      {tree.unmappedRooms.length > 0 && (
        <div className='space-y-1.5'>
          <p className='text-xs font-medium text-slate-500'>Rooms without a floor</p>
          <div className='flex flex-wrap gap-1.5'>
            {tree.unmappedRooms.map((r) => (
              <RoomChoice key={r.id} room={r} selected={r.id === value} onSelect={() => onChange(r.id)} inline />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function Step({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className='min-w-0 space-y-1.5'>
      <h4 className='truncate text-xs font-medium text-slate-500'>{title}</h4>
      <div className='max-h-56 space-y-1.5 overflow-y-auto pr-0.5'>{children}</div>
    </section>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className='rounded-lg border border-dashed px-3 py-4 text-center text-xs text-slate-500'>{children}</p>
}

function RoomChoice({ room, selected, onSelect, inline }: { room: LocationRoom; selected: boolean; onSelect: () => void; inline?: boolean }) {
  return (
    <button
      type='button'
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'flex items-center gap-2 rounded-xl border px-2.5 py-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        inline ? 'w-auto' : 'w-full',
        selected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-slate-200 bg-white hover:border-slate-300',
      )}
    >
      <DoorOpen className={cn('h-4 w-4 shrink-0', selected ? 'text-primary' : 'text-slate-400')} />
      <span className='min-w-0'>
        <span className='block truncate text-sm font-medium text-slate-900'>{room.label}</span>
        {!inline && (
          <span className='block truncate text-[11px] text-slate-500'>
            {room.name ?? room.typeName}
            {room.units.length > 0 && ` · ${room.units.length} in it`}
          </span>
        )}
      </span>
    </button>
  )
}
